/* eslint-disable max-lines */
import React, { FC, useMemo, useState } from 'react';

import { Trans, useTranslation } from '@modernloop/i18n';
import {
  ALL_FEATURE_FLAGS,
  FeatureFlagBool,
  FeatureFlagNames,
  JSON_FEATURE_FLAGS,
  LocalStorageFlags,
  RawFeatureFlagMap,
  useFeatureFlagLocal,
} from '@modernloop/shared/feature-flag';
import { CopyIcon, InfoIcon, OpenInNewTabIcon, QuestionCircleIcon } from '@modernloop/shared/icons';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
// Sanctioned vendor touchpoint: this debug-only tab lists ALL raw LaunchDarkly
// flags, which the typed shared feature-flag hooks intentionally cannot express.
// eslint-disable-next-line no-restricted-imports
import { useFlags } from 'launchdarkly-react-client-sdk';
import { has } from 'lodash';
import { useSnackbar } from 'notistack';

const LaunchDarklyEnvMap = {
  development: 'test',
  staging: 'staging',
  production: 'production',
};

const isPlainObject = (val) => {
  return !!val && typeof val === 'object' && val.constructor === Object;
};

const sanitizeFfId = (flagName: string) => flagName.replace(/[^a-zA-Z0-9_-]/g, '_');
const ff = (flagName: string, suffix: string) => `app-dev-tools-modal-ff-${sanitizeFfId(flagName)}-${suffix}`;
export const getOverrideEditorValue = (overrideValue: unknown, launchDarklyValue: unknown): unknown => {
  return overrideValue !== undefined ? overrideValue : (launchDarklyValue ?? '');
};

/** Validates the editable representation while preserving a valid JSON null override. */
export const validateJsonOverrideValue = (flagName: string, value: unknown): void => {
  if (value === null) {
    return;
  }
  if (typeof value !== 'string') {
    throw new Error(`Feature flag ${flagName} must be a JSON string or null.`);
  }
  JSON.parse(value);
};

const FeatureFlagsTab: FC = () => {
  const { t } = useTranslation();
  const ldLink = `https://app.launchdarkly.com/default/${LaunchDarklyEnvMap[import.meta.env.MODE || '']}/features`;

  const { enqueueSnackbar } = useSnackbar();

  // all flags in LD
  const launchDarklyFlags = useFlags();

  // flags from localStorage
  const { localStorageFeatureFlags, overrideStore, setLocalStorageFeatureFlags } = useFeatureFlagLocal();

  // flags that are displayed in the Feature Flags tab
  const [overrideFlags, setOverrideFlags] = useState<LocalStorageFlags>(
    localStorageFeatureFlags || ({} as LocalStorageFlags)
  );
  const overrideByFlagName = useMemo(() => {
    return Object.fromEntries(overrideStore.entries.map((entry) => [entry.override.key, entry]));
  }, [overrideStore.entries]);

  const commitOverrideFlags = (flags: RawFeatureFlagMap) => {
    try {
      JSON_FEATURE_FLAGS.forEach((flagName) => {
        if (has(flags, flagName)) {
          const rawFlagValue = flags[flagName];
          validateJsonOverrideValue(flagName, rawFlagValue);
        }
      });
      setOverrideFlags(setLocalStorageFeatureFlags(flags));
      enqueueSnackbar(t('hiring-dashboard-dev-tools:dev-tools.featureFlags.saved'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('hiring-dashboard-dev-tools:dev-tools.featureFlags.invalidJson', { error: String(error) }), {
        variant: 'error',
      });
    }
  };

  const handleOverrideUpdate = (flagName: FeatureFlagNames, overrideOn: boolean) => {
    if (overrideOn) {
      // when turning on the override, set it to the same value as we have in LD
      commitOverrideFlags({
        ...overrideFlags,
        [flagName]: isPlainObject(launchDarklyFlags[flagName])
          ? JSON.stringify(launchDarklyFlags[flagName])
          : launchDarklyFlags[flagName],
      });
    }
    if (!overrideOn) {
      // when turning off the override, remove the flag from the override object
      const { [flagName]: _removed, ...rest } = overrideFlags;
      commitOverrideFlags({ ...rest } as LocalStorageFlags);
    }
  };

  const handleFlagValueUpdate = (flagName: FeatureFlagNames, value: unknown) => {
    commitOverrideFlags({ ...overrideFlags, [flagName]: value });
  };

  const handleResetAllOverrides = () => {
    commitOverrideFlags({} as LocalStorageFlags);
  };

  const handleCopyAgentCommand = (flagName: FeatureFlagNames, flagValue: unknown) => {
    const quotedFlagName = JSON.stringify(flagName);
    let command = '';

    if (typeof flagValue === 'boolean') {
      command = `window.__modernloopDev.featureFlags.setBoolean(${quotedFlagName}, true, { reason: "manual override" })`;
    } else if (typeof flagValue === 'number') {
      command = `window.__modernloopDev.featureFlags.setNumber(${quotedFlagName}, ${JSON.stringify(
        flagValue
      )}, { reason: "manual override" })`;
    } else if (typeof flagValue === 'string') {
      command = `window.__modernloopDev.featureFlags.setString(${quotedFlagName}, ${JSON.stringify(
        flagValue
      )}, { reason: "manual override" })`;
    } else {
      command = `window.__modernloopDev.featureFlags.setJson(${quotedFlagName}, ${JSON.stringify(
        flagValue
      )}, { reason: "manual override" })`;
    }

    navigator.clipboard.writeText(command);
    enqueueSnackbar(t('hiring-dashboard-dev-tools:dev-tools.featureFlags.copiedToClipboard'), { variant: 'success' });
  };

  return (
    <Stack spacing={1} overflow="auto" width="100%">
      <Stack gap={1}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h4">
            <Trans
              i18nKey="hiring-dashboard-dev-tools:dev-tools.featureFlags.title"
              values={{ environment: import.meta.env.MODE }}
              components={{ strong: <strong /> }}
            />
          </Typography>

          <Link
            id="app-dev-tools-modal-ff-launch-darkly-link"
            data-testid="app-dev-tools-modal-ff-launch-darkly-link"
            href={ldLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('hiring-dashboard-dev-tools:dev-tools.featureFlags.launchDarklyLink')} <OpenInNewTabIcon />
          </Link>
        </Stack>

        <Stack direction="row" justifyContent="space-between">
          <Alert severity="info">
            <Typography variant="body2">
              {t('hiring-dashboard-dev-tools:dev-tools.featureFlags.refreshNotice')}
            </Typography>
          </Alert>
          <Button
            id="app-dev-tools-modal-ff-reset-all"
            data-testid="app-dev-tools-modal-ff-reset-all"
            onClick={handleResetAllOverrides}
          >
            {t('hiring-dashboard-dev-tools:dev-tools.featureFlags.resetAllOverrides')}
          </Button>
        </Stack>
      </Stack>
      <Box pb="80px">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('hiring-dashboard-dev-tools:dev-tools.featureFlags.columnFlagName')}</TableCell>
              <TableCell>{t('hiring-dashboard-dev-tools:dev-tools.featureFlags.columnValueInLD')}</TableCell>
              <TableCell>{t('hiring-dashboard-dev-tools:dev-tools.featureFlags.columnOverrideOn')}</TableCell>
              <TableCell>{t('hiring-dashboard-dev-tools:dev-tools.featureFlags.columnOverrideValue')}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={2}>
                  {t('hiring-dashboard-dev-tools:dev-tools.featureFlags.columnUsedInBranch')}
                  <Tooltip title={t('hiring-dashboard-dev-tools:dev-tools.featureFlags.usedInBranchTooltip')}>
                    <IconButton
                      id="app-dev-tools-modal-ff-used-in-branch-tooltip"
                      data-testid="app-dev-tools-modal-ff-used-in-branch-tooltip"
                      sx={{ py: '0px', px: '2px' }}
                    >
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {Object.entries(launchDarklyFlags)
              .sort()
              .map(([flagName, flagValue]) => {
                const flagLink = `${ldLink}/${flagName}/targeting`;
                const usedInBranch = ALL_FEATURE_FLAGS.includes(flagName as FeatureFlagBool);
                const overrideEnabled = has(overrideFlags, flagName);
                const overrideEntry = overrideByFlagName[flagName];

                return (
                  <TableRow key={flagName} hover id={ff(flagName, 'row')} data-testid={ff(flagName, 'row')}>
                    <TableCell>
                      <Link
                        id={ff(flagName, 'ld-targeting-link')}
                        data-testid={ff(flagName, 'ld-targeting-link')}
                        href={flagLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {flagName}
                      </Link>
                      <IconButton
                        id={ff(flagName, 'copy-name')}
                        data-testid={ff(flagName, 'copy-name')}
                        onClick={() => {
                          navigator.clipboard.writeText(flagName);
                          enqueueSnackbar(t('hiring-dashboard-dev-tools:dev-tools.featureFlags.copiedToClipboard'), {
                            variant: 'success',
                          });
                        }}
                      >
                        <CopyIcon />
                      </IconButton>
                      {usedInBranch && (
                        <IconButton
                          id={ff(flagName, 'copy-agent-command')}
                          data-testid={ff(flagName, 'copy-agent-command')}
                          onClick={() => {
                            handleCopyAgentCommand(flagName as FeatureFlagNames, flagValue);
                          }}
                        >
                          <CopyIcon />
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      {typeof flagValue === 'boolean' && (
                        <Chip label={JSON.stringify(flagValue)} color={flagValue === true ? 'success' : 'error'} />
                      )}

                      {(typeof flagValue === 'string' || typeof flagValue === 'number') && (
                        <TextField
                          disabled
                          value={JSON.stringify(flagValue)}
                          id={ff(flagName, 'ld-scalar-display')}
                          inputProps={{ 'data-testid': ff(flagName, 'ld-scalar-display') }}
                        />
                      )}

                      {isPlainObject(flagValue) && (
                        <Stack direction="row" width="100%">
                          <TextField
                            multiline
                            disabled
                            value={JSON.stringify(flagValue)}
                            id={ff(flagName, 'ld-json-display')}
                            inputProps={{ 'data-testid': ff(flagName, 'ld-json-display') }}
                          />
                          <IconButton
                            id={ff(flagName, 'copy-ld-json')}
                            data-testid={ff(flagName, 'copy-ld-json')}
                            onClick={() => {
                              navigator.clipboard.writeText(JSON.stringify(flagValue));
                              enqueueSnackbar(
                                t('hiring-dashboard-dev-tools:dev-tools.featureFlags.copiedJsonToClipboard'),
                                { variant: 'success' }
                              );
                            }}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      {usedInBranch && (
                        <FormControlLabel
                          sx={{
                            justifyContent: 'space-between',
                          }}
                          control={
                            <Switch
                              checked={overrideFlags[flagName] !== undefined}
                              inputProps={
                                {
                                  id: ff(flagName, 'override-toggle'),
                                  'data-testid': ff(flagName, 'override-toggle'),
                                } as React.InputHTMLAttributes<HTMLInputElement>
                              }
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                handleOverrideUpdate(flagName as FeatureFlagNames, event.target.checked);
                              }}
                            />
                          }
                          labelPlacement="start"
                          label={undefined}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {overrideEntry && (
                        <Stack direction="row" pb={1}>
                          <Chip size="small" label={overrideEntry.source} />
                        </Stack>
                      )}
                      {/* Boolean flag */}
                      {typeof launchDarklyFlags[flagName] === 'boolean' && usedInBranch && overrideEnabled && (
                        <Stack direction="row" justifyContent="space-evenly">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  overrideEnabled ? (overrideFlags[flagName] as boolean) : launchDarklyFlags[flagName]
                                }
                                inputProps={
                                  {
                                    id: ff(flagName, 'override-bool'),
                                    'data-testid': ff(flagName, 'override-bool'),
                                  } as React.InputHTMLAttributes<HTMLInputElement>
                                }
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                  handleFlagValueUpdate(flagName as FeatureFlagNames, event.target.checked);
                                }}
                                disabled={overrideFlags[flagName] === undefined}
                              />
                            }
                            labelPlacement="start"
                            label={undefined}
                          />
                          <Chip
                            color={overrideFlags[flagName] === true ? 'success' : 'error'}
                            label={JSON.stringify(overrideFlags[flagName])}
                          />
                        </Stack>
                      )}

                      {/* JSON flag */}
                      {isPlainObject(launchDarklyFlags[flagName]) && usedInBranch && (
                        <FormControlLabel
                          control={
                            <TextField
                              multiline
                              value={getOverrideEditorValue(
                                overrideFlags[flagName],
                                JSON.stringify(launchDarklyFlags[flagName])
                              )}
                              id={ff(flagName, 'override-json')}
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                handleFlagValueUpdate(flagName as FeatureFlagNames, event.target.value);
                              }}
                              disabled={overrideFlags[flagName] === undefined}
                              label={undefined}
                              inputProps={{ 'data-testid': ff(flagName, 'override-json') }}
                            />
                          }
                          labelPlacement="start"
                          label={undefined}
                        />
                      )}

                      {/* String flag */}
                      {typeof launchDarklyFlags[flagName] === 'string' && usedInBranch && (
                        <FormControlLabel
                          control={
                            <TextField
                              value={getOverrideEditorValue(overrideFlags[flagName], launchDarklyFlags[flagName])}
                              id={ff(flagName, 'override-string')}
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                handleFlagValueUpdate(flagName as FeatureFlagNames, event.target.value);
                              }}
                              disabled={overrideFlags[flagName] === undefined}
                              inputProps={{ 'data-testid': ff(flagName, 'override-string') }}
                            />
                          }
                          labelPlacement="start"
                          label={undefined}
                        />
                      )}

                      {/* Number flag */}
                      {typeof launchDarklyFlags[flagName] === 'number' && usedInBranch && (
                        <FormControlLabel
                          control={
                            <TextField
                              inputProps={{
                                inputMode: 'numeric',
                                'data-testid': ff(flagName, 'override-number'),
                              }}
                              id={ff(flagName, 'override-number')}
                              value={getOverrideEditorValue(overrideFlags[flagName], launchDarklyFlags[flagName])}
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                handleFlagValueUpdate(flagName as FeatureFlagNames, event.target.value);
                              }}
                              disabled={overrideFlags[flagName] === undefined}
                            />
                          }
                          labelPlacement="start"
                          label={undefined}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center">
                        {usedInBranch ? '✅' : '❌'}
                        {flagName.indexOf('org_') === 0 && !usedInBranch && (
                          <Tooltip title={t('hiring-dashboard-dev-tools:dev-tools.featureFlags.backendOnlyTooltip')}>
                            <IconButton
                              id={ff(flagName, 'backend-only-tooltip')}
                              data-testid={ff(flagName, 'backend-only-tooltip')}
                              sx={{ py: '0px', px: '10px' }}
                            >
                              <QuestionCircleIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
};

export default FeatureFlagsTab;
